import { useState } from 'react';
import Notice from './Notice.jsx';
import { api } from '../lib/api.js';
import {
  TRUST_STACK_VERSION, collectorClientName, trustScopeMatches, trustStackOutdated, trustStackName, trustStackUpdateSteps,
} from '../../../shared/trust-stack.mjs';

/**
 * Customer isolation (trust stack version 3, self-service plan S4): the notice that asks the customer admin to update
 * the onGuard access role (the trust stack) in their AWS account, with the whole story (the reader may not be the AWS
 * administrator), and one action at a time: get the update link, open it in AWS CloudFormation, then check again.
 * Shown to the customer admin and to an operator (who sees the same facts). Nothing breaks until the update: the
 * portal keeps working with the older role.
 */
export function trustStackNoticeState(customer) {
  if (!customer?.trustRoleArn) return null;
  const clientName = collectorClientName(customer);
  const outdated = trustStackOutdated(customer) === true;
  const scopeMatches = trustScopeMatches(customer, clientName);
  if (!outdated && scopeMatches) return null;
  return { clientName, outdated, scopeMatches, version: customer.trustStackVersion ?? null };
}

export default function TrustStackNotice({ customer, onRefresh }) {
  const state = trustStackNoticeState(customer);
  const [link, setLink] = useState(null);
  // The link's template fills in ClientName; only when the portal could not publish that copy (prefilled false) is the name typed.
  const [prefilled, setPrefilled] = useState(true);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  if (!state) return null;

  async function getLink() {
    setBusy(true); setMessage(null);
    try {
      const res = await api.getTrustLaunch(customer.customerId);
      // Only ever an AWS CloudFormation console link (defense in depth: the page renders it as a link).
      if (res?.mode !== 'update' || !String(res.launchUrl ?? '').startsWith('https://us-east-1.console.aws.amazon.com/cloudformation/')) {
        throw new Error('The update link is not available right now.');
      }
      setPrefilled(res.prefilled !== false);
      setLink(res.launchUrl);
    } catch (e) {
      setMessage(e.message || 'The update link is not available right now.');
    } finally {
      setBusy(false);
    }
  }

  async function checkAgain() {
    setBusy(true); setMessage(null);
    try {
      const res = await api.checkTrustStack(customer.customerId);
      if (res?.trustStack?.updateNeeded) setMessage('onGuard still sees the older access role. If the stack shows UPDATE_COMPLETE, try again in a minute.');
      onRefresh?.();
    } catch (e) {
      setMessage(e.message || 'Checking failed. Try again in a minute.');
    } finally {
      setBusy(false);
    }
  }

  let action;
  if (!link) {
    action = <button type="button" className="btn-primary text-xs" onClick={getLink} disabled={busy}>{busy ? 'Preparing...' : 'Get the update link'}</button>;
  } else if (!opened) {
    action = (
      <a className="btn-primary text-xs" href={link} target="_blank" rel="noopener noreferrer" onClick={() => setOpened(true)}>
        Open the update in AWS CloudFormation
      </a>
    );
  } else {
    action = <button type="button" className="btn-secondary text-xs" onClick={checkAgain} disabled={busy}>{busy ? 'Checking...' : 'I updated it, check again'}</button>;
  }

  return (
    <Notice tone="warn" titleAs="h3" title="Update the onGuard access role in your AWS account" action={action} testId="trust-stack-notice" className="mt-3">
      {state.outdated ? (
        <p>
          Your AWS account has an older onGuard access role (version {state.version ?? 'unknown'}). It is allowed to manage every onGuard
          collector in the account, not only yours. Version {TRUST_STACK_VERSION} limits it to your own collector,{' '}
          <span className="font-mono">{state.clientName}</span>, and its data. onGuard keeps working until you update.
        </p>
      ) : (
        <p>
          The onGuard access role in your AWS account is set up for another collector name
          (<span className="font-mono">{customer.trustStackClientName}</span>), so it cannot manage your collector,{' '}
          <span className="font-mono">{state.clientName}</span>. Update it with the right name.
        </p>
      )}
      <p className="mt-1">
        Someone who can update AWS CloudFormation stacks and IAM roles in this account does it, in about a minute, on the
        stack <span className="font-mono">{trustStackName(customer.customerId)}</span> (us-east-1):
      </p>
      <ol className="mt-1 list-decimal pl-5" data-testid="trust-stack-steps">
        {trustStackUpdateSteps(state.clientName, { prefilled }).map((s) => <li key={s}>{s}</li>)}
      </ol>
      {message && <p className="mt-1 text-ipc-ink" role="status">{message}</p>}
    </Notice>
  );
}
